import { useEffect, useMemo, useRef, useState } from 'react';
import { api, imageUrl, loadWithRetry, peek, uploadToCloudinary } from './api';
import { LoadingSpinner, Skeleton, Toaster, toast } from './feedback';
import RenameDialog from './RenameDialog';
import SearchBar from './SearchBar';
import LoginPage from './LoginPage';
import { matchesFields } from './search';
import { KAI_LANGS, KaiLangContext, initialLang, saveLang, translator, useT } from './kaiI18n';
import { toBrowserPath } from './config';
import AnalyticsPage from './AnalyticsPage';
import './Kai.css';

// Ảnh nhỏ cho lưới / hàng thumbnail: ảnh thì thu nhỏ bằng imageUrl, khung hình video (.jpg) thì chèn biến đổi tương tự
function thumb(url, width) {
  if (url?.includes('/video/upload/')) return url.replace('/video/upload/', `/video/upload/f_auto,q_auto,c_limit,w_${width}/`);
  return imageUrl(url, width);
}

const isVideo = (file) => file.type.startsWith('video/');

// Đăng nhiều ảnh/video vào bộ sưu tập: tải thẳng lên Cloudinary rồi lưu lại. onProgress({ done, total, percent })
async function uploadFiles(albumId, files, onProgress, onAdded) {
  let failed = 0;
  for (let i = 0; i < files.length; i++) {
    onProgress({ done: i, total: files.length, percent: 0 });
    try {
      // Mỗi file xin chữ ký mới: chữ ký Cloudinary chỉ dùng được 1 giờ, đăng nhiều video lâu hơn thế vẫn không lỗi
      const signature = await api.kaiUploadSignature();
      const result = await uploadToCloudinary(files[i], signature, (p) =>
        onProgress({ done: i, total: files.length, percent: Math.round(p * 100) })
      );
      onAdded?.(
        await api.addKaiMedia(albumId, {
          type: result.resource_type === 'video' ? 'VIDEO' : 'IMAGE',
          url: result.secure_url,
          publicId: result.public_id,
        })
      );
    } catch {
      failed++;
    }
  }
  return failed;
}

// Chia sẻ link: điện thoại mở bảng chia sẻ (Zalo, Messenger…), máy tính thì sao chép link
async function shareLink(url, title, t) {
  try {
    if (navigator.share) {
      await navigator.share({ title, url });
      return;
    }
  } catch (e) {
    if (e.name === 'AbortError') return; // người dùng tự đóng bảng chia sẻ
  }
  try {
    await navigator.clipboard.writeText(url);
    toast(t('linkCopied'));
  } catch {
    window.prompt(t('share'), url);
  }
}

// Trang /kai: thư viện ảnh, video tranh cho khách xem (sau này gắn tên miền kairepaint.com).
// /kai = trang chủ (ảnh lớn + lưới bộ sưu tập), /kai/album/:id = lưới ảnh của bộ sưu tập; ?anh=<id> mở thẳng một ảnh.
// Admin đăng nhập ở /kai/dang-nhap (Google, chung danh sách admin với kho chứng nhận) để tạo/sửa/xoá bộ sưu tập, đăng ảnh, video.
const AFTER_LOGIN_KEY = 'kai-after-login'; // trang Kai cần quay lại sau khi đăng nhập
const GOOGLE_LOCALES = { vi: 'vi', en: 'en', ja: 'ja', zh: 'zh-CN' };

export default function KaiPage({ path, location, navigate, isAdmin, onLogout }) {
  const albumId = path.match(/^\/kai\/album\/(\d+)$/)?.[1];
  const isLoginPage = path === '/kai/dang-nhap';
  const isAnalyticsPage = path === '/kai/thong-ke';
  const openMediaId = Number(new URLSearchParams(location.split('?')[1] || '').get('anh')) || null;
  const [lang, setLang] = useState(initialLang);
  const t = useMemo(() => translator(lang), [lang]);

  // Dữ liệu dùng chung: danh sách bộ sưu tập + mọi ảnh/video (cho tìm kiếm và ảnh lớn đầu trang)
  const [albums, setAlbums] = useState(null);
  const [allMedia, setAllMedia] = useState([]);
  const [settings, setSettings] = useState({}); // { heroImageUrl, heroFit, heroPosition, taglines }
  const [loadError, setLoadError] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [waking, setWaking] = useState(false); // máy chủ đang khởi động lại, đang tự thử lại
  const reload = () => {
    // Lần đầu mở trang: hiện ngay bản đã lưu (nếu có), bản mới từ máy chủ thay vào sau
    if (!albums) {
      const cached = peek(() => api.kaiAlbums());
      if (cached) {
        setAlbums(cached);
        setAllMedia(peek(() => api.kaiAllMedia()) || []);
        setSettings(peek(() => api.kaiSettings()) || {});
      }
    }
    return loadWithRetry(() => api.kaiAlbums(), () => setWaking(true))
      .then(async (a) => {
        // Danh sách ảnh (tìm kiếm) và cài đặt đầu trang: lỗi thì bỏ qua, trang vẫn hiện bộ sưu tập
        const [m, st] = await Promise.all([api.kaiAllMedia().catch(() => []), api.kaiSettings().catch(() => ({}))]);
        setAlbums(a);
        setAllMedia(m);
        setSettings(st);
      })
      .catch(() => setLoadError(true))
      .finally(() => setWaking(false));
  };

  // Phiên đăng nhập hết hạn (sau 24 giờ): báo theo ngôn ngữ đang chọn
  useEffect(() => {
    const onExpired = () => toast(t('sessionExpired'));
    window.addEventListener('session-expired', onExpired);
    return () => window.removeEventListener('session-expired', onExpired);
  }, [t]);

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  // Tab trình duyệt: tên + biểu tượng Kai khi ở trang này, trả lại như cũ khi rời đi
  useEffect(() => {
    document.title = 'KAI Repaint';
    const icon = document.querySelector('link[rel="icon"]');
    const oldIcon = icon?.getAttribute('href');
    icon?.setAttribute('href', '/kai-favicon.png');
    return () => {
      if (icon && oldIcon) icon.setAttribute('href', oldIcon);
    };
  }, []);
  useEffect(() => {
    document.documentElement.lang = lang;
    return () => {
      document.documentElement.lang = 'vi'; // rời trang Kai thì trả về tiếng Việt cho kho chứng nhận
    };
  }, [lang]);

  // Tìm kiếm: bộ sưu tập theo tên, ảnh/video theo tiêu đề, mô tả
  const searchItems = useMemo(
    () => [
      ...(albums || []).map((a) => ({ kind: 'album', key: `a${a.id}`, album: a })),
      ...allMedia.filter((m) => m.title || m.description).map((m) => ({ kind: 'media', key: `m${m.id}`, media: m })),
    ],
    [albums, allMedia]
  );
  const matchItem = (item, q) =>
    item.kind === 'album'
      ? matchesFields([item.album.name], q)
      : matchesFields([item.media.title, item.media.description, item.media.albumName], q);
  const describeItem = (item) =>
    item.kind === 'album'
      ? { key: item.key, title: item.album.name, sub: t('mediaCount', { n: item.album.mediaCount }) }
      : { key: item.key, title: item.media.title || item.media.description, sub: t('inCollection', { name: item.media.albumName }) };
  // Câu giới thiệu do admin lưu: tiếng đang chọn chưa có thì dùng câu tiếng Việt; chưa lưu gì thì không hiện
  const tagline = settings.taglines?.[lang] || settings.taglines?.vi || '';

  // Đăng nhập ngay trong trang Kai, xong thì quay lại đúng trang đang xem
  const goLogin = () => {
    try {
      sessionStorage.setItem(AFTER_LOGIN_KEY, location);
    } catch {}
    navigate('/kai/dang-nhap');
  };
  const afterLogin = () => {
    let next = '/kai';
    try {
      next = sessionStorage.getItem(AFTER_LOGIN_KEY) || '/kai';
      sessionStorage.removeItem(AFTER_LOGIN_KEY);
    } catch {}
    navigate(next);
  };

  const pickItem = (item) => {
    setSearchQuery('');
    if (item.kind === 'album') navigate(`/kai/album/${item.album.id}`);
    else navigate(`/kai/album/${item.media.albumId}?anh=${item.media.id}`);
  };

  return (
    <KaiLangContext.Provider value={t}>
      <div className="kai-page">
        <header className="kai-header">
          <button className="kai-brand" onClick={() => navigate('/kai')} aria-label="KAI Repaint">
            <img src="/kai-logo.jpg" alt="" />
            <span className="kai-wordmark">
              KAI <small>Repaint</small>
            </span>
          </button>
          <div className="kai-header-search">
            <SearchBar
              items={searchItems}
              matches={matchItem}
              describe={describeItem}
              query={searchQuery}
              onQueryChange={setSearchQuery}
              onPick={pickItem}
              placeholder={t('searchPlaceholder')}
              emptyText={t('searchEmpty')}
            />
          </div>
          <div className="kai-header-right">
            {/* Đổi ngôn ngữ (biểu tượng quả địa cầu như trên điện thoại): toàn bộ chữ trên trang chuyển theo */}
            <label className="kai-lang-wrap" title={t('language')}>
              <svg className="kai-lang-icon" viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="12" cy="12" r="9" />
                <path d="M3 12h18M12 3c2.5 2.6 3.8 5.6 3.8 9s-1.3 6.4-3.8 9c-2.5-2.6-3.8-5.6-3.8-9S9.5 5.6 12 3z" />
              </svg>
              <select
                className="kai-lang"
                value={lang}
                onChange={(e) => {
                  setLang(e.target.value);
                  saveLang(e.target.value);
                }}
                aria-label={t('language')}
              >
                {KAI_LANGS.map((l) => (
                  <option key={l.code} value={l.code}>
                    {l.label}
                  </option>
                ))}
              </select>
            </label>
            {isAdmin ? (
              <>
                <button
                  className="kai-link"
                  onClick={() => navigate(isAnalyticsPage ? '/kai' : '/kai/thong-ke')}
                >
                  {isAnalyticsPage ? 'Trang chủ' : 'Thống kê'}
                </button>
                <button
                  className="kai-link"
                  onClick={() => {
                    onLogout();
                    toast(t('loggedOut'));
                  }}
                >
                  {t('logout')}
                </button>
              </>
            ) : (
              !isLoginPage && (
                <button className="kai-link" onClick={goLogin}>
                  {t('login')}
                </button>
              )
            )}
          </div>
        </header>

        <main className="kai-main">
          {waking && <p className="kai-waking">{t('serverWaking')}</p>}
          {isAnalyticsPage && isAdmin ? (
            <AnalyticsPage />
          ) : isLoginPage ? (
            <div className="kai-login">
              <button className="kai-back" onClick={() => navigate('/kai')}>
                {t('backHome')}
              </button>
              <img className="kai-login-logo" src="/kai-logo-lg.jpg" alt="KAI Repaint" />
              {isAdmin ? (
                <AlreadyLoggedIn onDone={afterLogin} />
              ) : (
                <LoginPage
                  key={lang} // đổi ngôn ngữ thì vẽ lại nút Google theo ngôn ngữ mới
                  locale={GOOGLE_LOCALES[lang]}
                  texts={{
                    title: t('loginTitle'),
                    subtitle: t('loginSubtitle'),
                    loadingButton: t('loginLoading'),
                    signingIn: t('signingIn'),
                    invalid: t('loginInvalid'),
                    success: t('loginSuccess'),
                  }}
                  onSuccess={afterLogin}
                />
              )}
            </div>
          ) : loadError ? (
            <p className="error kai-empty">{t('loadError')}</p>
          ) : albumId ? (
            <AlbumView
              key={location}
              id={Number(albumId)}
              openMediaId={openMediaId}
              isAdmin={isAdmin}
              navigate={navigate}
              onChanged={reload}
              onDeleted={(albumId) => setAlbums((list) => list.filter((a) => a.id !== albumId))}
            />
          ) : (
            <Home albums={albums} settings={settings} tagline={tagline} isAdmin={isAdmin} navigate={navigate} onChanged={reload} />
          )}
        </main>

        <footer className="kai-footer">
          <img src="/kai-logo.jpg" alt="" />
          <strong>KAI Repaint</strong>
          {tagline && <p>{tagline}</p>}
          <small>
            © {new Date().getFullYear()} KAI Repaint. {t('rights')}
          </small>
        </footer>
        <Toaster />
      </div>
    </KaiLangContext.Provider>
  );
}

// Đã đăng nhập mà vẫn mở trang đăng nhập (vd bấm Back) thì quay lại luôn
function AlreadyLoggedIn({ onDone }) {
  useEffect(() => {
    onDone();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

// Trang chủ /kai: phần đầu trang (ảnh nền do admin chọn) + lưới bộ sưu tập có sắp xếp
function Home({ albums, settings, tagline, isAdmin, navigate, onChanged }) {
  const t = useT();
  const [sort, setSort] = useState('newest');
  const [creating, setCreating] = useState(false);
  const [editingHero, setEditingHero] = useState(false);
  const sorted = useMemo(() => {
    if (!albums) return null;
    return sort === 'name' ? [...albums].sort((a, b) => a.name.localeCompare(b.name, 'vi')) : albums;
  }, [albums, sort]);

  return (
    <>
      <section className="kai-hero">
        {settings.heroImageUrl && <HeroBackground url={settings.heroImageUrl} fit={settings.heroFit} position={settings.heroPosition} />}
        {isAdmin && (
          <button className="kai-hero-edit" onClick={() => setEditingHero(true)}>
            ✎ {t('editHeader')}
          </button>
        )}
        <div className="kai-hero-content">
          <img className="kai-hero-logo" src="/kai-logo-lg.jpg" alt="KAI Repaint" />
          <div>
            <h1>KAI Repaint</h1>
            {tagline && <p>{tagline}</p>}
          </div>
        </div>
      </section>

      <div className="kai-contact">
        <span className="kai-contact-label">Liên hệ</span>
        <a className="kai-contact-link kai-contact-insta" href="https://www.instagram.com/kai.repaint" target="_blank" rel="noopener noreferrer">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 1.366.062 2.633.334 3.608 1.308.974.974 1.246 2.241 1.308 3.608.058 1.265.07 1.645.07 4.85s-.012 3.584-.07 4.85c-.062 1.366-.334 2.633-1.308 3.608-.974.974-2.241 1.246-3.608 1.308-1.265.058-1.645.07-4.85.07s-3.584-.012-4.85-.07c-1.366-.062-2.633-.334-3.608-1.308-.974-.974-1.246-2.241-1.308-3.608C2.175 15.584 2.163 15.204 2.163 12s.012-3.584.07-4.85c.062-1.366.334-2.633 1.308-3.608.974-.974 2.241-1.246 3.608-1.308C8.416 2.175 8.796 2.163 12 2.163zm0-2.163C8.741 0 8.333.014 7.053.072 5.197.157 3.355.673 2.014 2.014.673 3.355.157 5.197.072 7.053.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.085 1.856.601 3.698 1.942 5.038 1.341 1.341 3.183 1.857 5.038 1.942C8.333 23.986 8.741 24 12 24s3.668-.014 4.948-.072c1.856-.085 3.698-.601 5.038-1.942 1.341-1.341 1.857-3.183 1.942-5.038.058-1.28.072-1.689.072-4.948s-.014-3.667-.072-4.947c-.085-1.856-.601-3.698-1.942-5.038C20.698.673 18.856.157 17 .072 15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zm0 10.162a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/></svg>
          Instagram
        </a>
      </div>

      <div className="kai-section-head">
        <h2>{t('collections')}</h2>
        <div className="kai-section-tools">
          <label className="kai-sort">
            <span>{t('sortLabel')}</span>
            <select value={sort} onChange={(e) => setSort(e.target.value)}>
              <option value="newest">{t('sortNewest')}</option>
              <option value="name">{t('sortName')}</option>
            </select>
          </label>
          {isAdmin && (
            <button className="primary" onClick={() => setCreating(true)}>
              {t('newAlbum')}
            </button>
          )}
        </div>
      </div>

      {!sorted ? (
        <div className="kai-albums" aria-busy="true" aria-label={t('loading')}>
          <LoadingSpinner label={`${t('loading')}…`} />
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="kai-album">
              <Skeleton radius={18} style={{ width: '100%', aspectRatio: '1' }} />
              <Skeleton width="60%" height={18} style={{ marginTop: 12 }} />
            </div>
          ))}
        </div>
      ) : sorted.length === 0 ? (
        <p className="muted kai-empty">{t(isAdmin ? 'noAlbumsAdmin' : 'noAlbums')}</p>
      ) : (
        <div className="kai-albums">
          {sorted.map((a) => (
            <button key={a.id} className="kai-album" onClick={() => navigate(`/kai/album/${a.id}`)}>
              <span className="kai-album-cover">
                {a.coverUrl ? <img src={thumb(a.coverUrl, 600)} alt="" loading="lazy" /> : <span className="kai-album-empty">{t('noCover')}</span>}
                <span className="kai-album-badge">{a.mediaCount}</span>
              </span>
              <strong className="kai-album-name">{a.name}</strong>
              <span className="kai-album-count">{t('mediaCount', { n: a.mediaCount })}</span>
            </button>
          ))}
        </div>
      )}

      {editingHero && (
        <HeroEditor
          settings={settings}
          onDone={() => {
            setEditingHero(false);
            onChanged();
          }}
          onCancel={() => setEditingHero(false)}
        />
      )}
      {creating && (
        <RenameDialog
          title={t('createAlbum')}
          label={t('albumName')}
          cancelLabel={t('cancel')}
          saveLabel={t('create')}
          savingLabel={t('creating')}
          initial=""
          onSubmit={async (name) => {
            const album = await api.createKaiAlbum(name);
            toast(t('created', { name: album.name }));
            navigate(`/kai/album/${album.id}`);
          }}
          onCancel={() => setCreating(false)}
        />
      )}
    </>
  );
}

// Ảnh nền phần đầu trang: lấp đầy khung (canh theo vị trí đã chọn) hoặc hiện trọn ảnh trên nền là chính ảnh đó làm mờ
function HeroBackground({ url, fit = 'cover', position = '50% 50%' }) {
  const src = url.startsWith('blob:') ? url : imageUrl(url, 1800);
  return (
    <>
      {fit === 'contain' && <img className="kai-hero-blur" src={src} alt="" />}
      <img className="kai-hero-bg" src={src} alt="" style={{ objectFit: fit, objectPosition: position }} draggable={false} />
    </>
  );
}

// Chỉnh phần đầu trang: ảnh nền (chọn ảnh, kéo để canh, lấp đầy / hiện trọn) + câu giới thiệu từng ngôn ngữ
function HeroEditor({ settings, onDone, onCancel }) {
  const t = useT();
  const [file, setFile] = useState(null);
  const [fit, setFit] = useState(settings.heroFit || 'cover');
  const [position, setPosition] = useState(settings.heroPosition || '50% 50%');
  const [taglines, setTaglines] = useState({ ...settings.taglines });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [percent, setPercent] = useState(null);
  const drag = useRef(null);

  const preview = useMemo(() => (file ? URL.createObjectURL(file) : ''), [file]);
  useEffect(() => () => preview && URL.revokeObjectURL(preview), [preview]);
  const imageSrc = preview || settings.heroImageUrl;

  const pick = (e) => {
    const f = e.target.files[0];
    e.target.value = '';
    if (!f) return;
    setError('');
    setFile(f);
    setPosition('50% 50%');
  };

  // Kéo trên khung xem trước để chọn phần ảnh được giữ lại (chỉ khi lấp đầy khung)
  const onPointerDown = (e) => {
    if (fit !== 'cover' || !imageSrc) return;
    const [x, y] = position.split(' ').map(parseFloat);
    drag.current = { startX: e.clientX, startY: e.clientY, x, y, w: e.currentTarget.clientWidth, h: e.currentTarget.clientHeight };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d) return;
    const clamp = (v) => Math.round(Math.min(100, Math.max(0, v)));
    // kéo ảnh sang phải = xem phần bên trái nhiều hơn
    setPosition(`${clamp(d.x - ((e.clientX - d.startX) / d.w) * 150)}% ${clamp(d.y - ((e.clientY - d.startY) / d.h) * 150)}%`);
  };
  const onPointerUp = () => {
    drag.current = null;
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (file) {
        const signature = await api.kaiUploadSignature();
        const result = await uploadToCloudinary(file, signature, (p) => setPercent(Math.round(p * 100)));
        await api.setKaiHero({ url: result.secure_url, publicId: result.public_id });
      }
      await api.updateKaiSettings({ heroFit: fit, heroPosition: position, taglines });
      toast(t('headerSaved'));
      await onDone();
    } catch (err) {
      setError(err.message);
      setSaving(false);
      setPercent(null);
    }
  };

  const removeImage = async () => {
    if (!window.confirm(t('confirmRemoveCover'))) return;
    setSaving(true);
    try {
      await api.clearKaiHero();
      toast(t('coverRemoved'));
      await onDone();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  const frame = (className, interactive) => (
    <div
      className={`kai-hero-frame ${className} ${fit === 'cover' && imageSrc && interactive ? 'draggable' : ''}`}
      onPointerDown={interactive ? onPointerDown : undefined}
      onPointerMove={interactive ? onPointerMove : undefined}
      onPointerUp={interactive ? onPointerUp : undefined}
      onPointerCancel={interactive ? onPointerUp : undefined}
    >
      {imageSrc ? <HeroBackground url={imageSrc} fit={fit} position={position} /> : <span>{t('pickImage')}</span>}
    </div>
  );

  return (
    <div className="dialog-backdrop" onClick={saving ? undefined : onCancel}>
      <form className="dialog kai-hero-editor" onClick={(e) => e.stopPropagation()} onSubmit={save} aria-label={t('editHeader')}>
        <h2>{t('editHeader')}</h2>

        <div className="field">
          {t('heroImage')}
          <div className="kai-hero-previews">
            <div>
              <small>{t('previewDesktop')}</small>
              {frame('desktop', true)}
            </div>
            <div>
              <small>{t('previewMobile')}</small>
              {frame('mobile', false)}
            </div>
          </div>
          {imageSrc && fit === 'cover' && <p className="muted kai-hint">✥ {t('dragHint')}</p>}
          <div className="kai-hero-tools">
            {imageSrc && (
              <div className="kai-chips" role="group">
                <button type="button" className={`kai-chip ${fit === 'cover' ? 'active' : ''}`} onClick={() => setFit('cover')}>
                  {t('fitCover')}
                </button>
                <button type="button" className={`kai-chip ${fit === 'contain' ? 'active' : ''}`} onClick={() => setFit('contain')}>
                  {t('fitContain')}
                </button>
              </div>
            )}
            <label className="kai-file-btn">
              {imageSrc ? t('chooseOther') : t('pickImage')}
              <input type="file" accept="image/*" hidden onChange={pick} disabled={saving} />
            </label>
            {settings.heroImageUrl && !file && (
              <button type="button" className="danger" onClick={removeImage} disabled={saving}>
                {t('removeCover')}
              </button>
            )}
          </div>
        </div>

        <div className="field">
          {t('taglineLabel')}
          {KAI_LANGS.map((l) => (
            <label key={l.code} className={`kai-tagline-input ${l.code === 'vi' ? 'main' : ''}`}>
              <span>{l.label}</span>
              <input
                maxLength={200}
                value={taglines[l.code] || ''}
                onChange={(e) => setTaglines({ ...taglines, [l.code]: e.target.value })}
              />
            </label>
          ))}
        </div>

        {error && <p className="error">{error}</p>}
        <div className="dialog-actions">
          <button type="button" onClick={onCancel} disabled={saving}>
            {t('cancel')}
          </button>
          <button type="submit" className="primary" disabled={saving}>
            {percent !== null ? `${t('saving')} ${percent}%` : saving ? t('saving') : t('save')}
          </button>
        </div>
      </form>
    </div>
  );
}

const FILTERS = [
  { key: 'all', label: 'filterAll', test: () => true },
  { key: 'IMAGE', label: 'filterPhotos', test: (m) => m.type === 'IMAGE' },
  { key: 'VIDEO', label: 'filterVideos', test: (m) => m.type === 'VIDEO' },
];

// Trang bộ sưu tập: lưới ảnh vuông thu gọn (mới nhất lên đầu) như album trên điện thoại; bấm ảnh để xem toàn màn hình
function AlbumView({ id, openMediaId, isAdmin, navigate, onChanged, onDeleted }) {
  const t = useT();
  const [album, setAlbum] = useState(undefined); // undefined = đang tải, null = không tìm thấy
  const [filter, setFilter] = useState('all');
  const [viewingId, setViewingId] = useState(openMediaId); // ảnh đang mở toàn màn hình (theo id), null = đang xem lưới
  const [dialog, setDialog] = useState(null); // 'rename' | 'info'
  const [upload, setUpload] = useState(null); // { done, total, percent } khi đang tải lên
  const [picked, setPicked] = useState(null); // null = không ở chế độ chọn; Set id ảnh/video đang chọn để xoá
  const [deleting, setDeleting] = useState(false);
  const fileInput = useRef(null);

  const load = () =>
    loadWithRetry(() => api.kaiAlbum(id))
      .then(setAlbum)
      .catch((e) => (e.status === 404 ? setAlbum(null) : toast(t('albumLoadError', { error: e.message }))));

  useEffect(() => {
    const cached = peek(() => api.kaiAlbum(id)); // mở lại bộ sưu tập đã xem: hiện ngay bản đã lưu
    if (cached) setAlbum(cached);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const allMedia = album?.media || [];
  const media = allMedia.filter(FILTERS.find((f) => f.key === filter).test);
  const viewingIndex = media.findIndex((m) => m.id === viewingId);
  const current = viewingIndex >= 0 ? media[viewingIndex] : null;

  // Link trên thanh địa chỉ luôn trỏ đúng ảnh đang xem, để chia sẻ hoặc tải lại vẫn mở đúng ảnh
  useEffect(() => {
    if (!album) return;
    const url = current ? `/kai/album/${id}?anh=${current.id}` : `/kai/album/${id}`;
    if (window.location.pathname + window.location.search !== toBrowserPath(url)) window.history.replaceState(null, '', toBrowserPath(url));
  }, [album, current, id]);

  // Sau mỗi thay đổi: chỗ vừa sửa đã cập nhật ngay trên màn hình; bộ sưu tập và trang chủ được lấy lại ngầm cho khớp
  const refresh = () => {
    load();
    onChanged();
  };
  const patchMedia = (fn) => setAlbum((a) => ({ ...a, media: fn(a.media) }));

  // Đăng thêm nhiều ảnh/video vào bộ sưu tập đang xem
  const handleFiles = async (e) => {
    const files = Array.from(e.target.files);
    e.target.value = '';
    const list = files;
    if (!list.length) return;

    let failed;
    try {
      // Ảnh mới nhất đứng đầu: đăng xong tấm nào hiện ngay tấm đó
      failed = await uploadFiles(id, list, setUpload, (m) => patchMedia((all) => [m, ...all]));
    } catch (err) {
      toast(t('uploadFailed', { error: err.message }));
      setUpload(null);
      return;
    }
    setUpload(null);
    toast(failed ? t('uploadedSome', { ok: list.length - failed, fail: failed }) : t('uploadedAll', { n: list.length }));
    refresh();
  };

  const removeAlbum = async () => {
    if (!window.confirm(t('confirmDeleteAlbum', { name: album.name, n: allMedia.length }))) return;
    try {
      await api.deleteKaiAlbum(id);
      toast(t('albumDeleted'));
      onDeleted(id);
      navigate('/kai');
    } catch (e) {
      toast(t('deleteError', { error: e.message }));
    }
  };

  const removeCurrent = async () => {
    if (!window.confirm(t('confirmDeleteMedia'))) return;
    try {
      await api.deleteKaiMedia(current.id);
      toast(t('deleted'));
      // Xoá xong thì đứng ở ảnh kế bên; hết ảnh thì đóng trình xem
      const next = media[viewingIndex + 1] || media[viewingIndex - 1];
      setViewingId(next ? next.id : null);
      patchMedia((all) => all.filter((m) => m.id !== current.id));
      refresh();
    } catch (e) {
      toast(t('deleteError', { error: e.message }));
    }
  };

  const togglePick = (mediaId) => {
    const next = new Set(picked);
    next.has(mediaId) ? next.delete(mediaId) : next.add(mediaId);
    setPicked(next);
  };

  // Xoá nhiều ảnh/video đã chọn (xoá luôn file trên Cloudinary)
  const removePicked = async () => {
    if (!window.confirm(t('confirmDeleteN', { n: picked.size }))) return;
    setDeleting(true);
    let failed = 0;
    const removed = new Set();
    for (const mediaId of picked) {
      try {
        await api.deleteKaiMedia(mediaId);
        removed.add(mediaId);
      } catch {
        failed++;
      }
    }
    setDeleting(false);
    toast(failed ? t('deletedSome', { ok: picked.size - failed, fail: failed }) : t('deletedN', { n: picked.size }));
    setPicked(null);
    patchMedia((all) => all.filter((m) => !removed.has(m.id)));
    refresh();
  };

  const back = (
    <button className="kai-back" onClick={() => navigate('/kai')}>
      {t('back')}
    </button>
  );
  if (album === undefined)
    return (
      <div aria-busy="true" aria-label={t('loading')}>
        <LoadingSpinner label={`${t('loading')}…`} />
        {back}
        <Skeleton width={260} height={36} style={{ margin: '12px 0 20px' }} />
        <div className="kai-grid">
          {Array.from({ length: 12 }, (_, i) => (
            <Skeleton key={i} radius={0} style={{ width: '100%', aspectRatio: '1' }} />
          ))}
        </div>
      </div>
    );
  if (album === null)
    return (
      <>
        {back}
        <p className="muted kai-empty">{t('notFound')}</p>
      </>
    );

  return (
    <>
      {back}
      <div className="kai-album-head">
        <div>
          <h1>{album.name}</h1>
          <span className="kai-album-count">{t('mediaCount', { n: allMedia.length })}</span>
        </div>
        <div className="kai-actions">
          {picked ? (
            <>
              <span className="kai-pick-count">{t('selectedN', { n: picked.size })}</span>
              <button onClick={() => setPicked(picked.size === media.length ? new Set() : new Set(media.map((m) => m.id)))} disabled={deleting}>
                {t('selectAll')}
              </button>
              <button onClick={() => setPicked(null)} disabled={deleting}>
                {t('cancel')}
              </button>
              <button className="danger" onClick={removePicked} disabled={deleting || picked.size === 0}>
                {deleting ? t('deleting') : t('deleteN', { n: picked.size })}
              </button>
            </>
          ) : (
            <button onClick={() => shareLink(window.location.origin + toBrowserPath(`/kai/album/${id}`), album.name, t)}>↗ {t('share')}</button>
          )}
          {isAdmin && !picked && (
            <>
              <button className="primary" onClick={() => fileInput.current.click()} disabled={!!upload}>
                {upload ? t('uploading', { i: upload.done + 1, n: upload.total, p: upload.percent }) : t('addMedia')}
              </button>
              <button onClick={() => setDialog('rename')}>{t('rename')}</button>
              {allMedia.length > 0 && (
                <button className="danger" onClick={() => setPicked(new Set())}>
                  {t('selectDelete')}
                </button>
              )}
              <button className="danger" onClick={removeAlbum}>
                {t('deleteAlbum')}
              </button>
              <input ref={fileInput} type="file" accept="image/*,video/*" multiple hidden onChange={handleFiles} />
            </>
          )}
        </div>
      </div>

      {allMedia.length > 0 && (
        <div className="kai-chips" role="group">
          {FILTERS.map((f) => (
            <button key={f.key} className={`kai-chip ${filter === f.key ? 'active' : ''}`} onClick={() => setFilter(f.key)}>
              {t(f.label)} <span>{allMedia.filter(f.test).length}</span>
            </button>
          ))}
        </div>
      )}

      {allMedia.length === 0 ? (
        <p className="muted kai-empty">{t(isAdmin ? 'emptyAlbumAdmin' : 'emptyAlbum')}</p>
      ) : media.length === 0 ? (
        <p className="muted kai-empty">{t('noMatch')}</p>
      ) : (
        <div className={`kai-grid ${picked ? 'picking' : ''}`}>
          {media.map((m, i) => (
            <button
              key={m.id}
              className={`kai-cell ${picked?.has(m.id) ? 'picked' : ''}`}
              onClick={() => (picked ? togglePick(m.id) : setViewingId(m.id))}
              aria-pressed={picked ? picked.has(m.id) : undefined}
              aria-label={t('photoN', { n: i + 1 })}
            >
              <img src={thumb(m.thumbnailUrl, 400)} alt={m.title || ''} loading="lazy" />
              {m.type === 'VIDEO' && <span className="kai-play">▶</span>}
              {picked && <span className="kai-check">{picked.has(m.id) ? '✓' : ''}</span>}
            </button>
          ))}
        </div>
      )}

      {current && (
        <MediaViewer
          media={media}
          index={viewingIndex}
          onIndexChange={(i) => setViewingId(media[i].id)}
          onClose={() => setViewingId(null)}
          onShare={() => shareLink(window.location.origin + toBrowserPath(`/kai/album/${id}?anh=${current.id}`), current.title || album.name, t)}
          isAdmin={isAdmin}
          onEdit={() => setDialog('info')}
          onDelete={removeCurrent}
        />
      )}

      {dialog === 'rename' && (
        <RenameDialog
          title={t('renameAlbum')}
          label={t('albumName')}
          cancelLabel={t('cancel')}
          saveLabel={t('save')}
          savingLabel={t('saving')}
          initial={album.name}
          onSubmit={async (name) => {
            await api.renameKaiAlbum(id, name);
            toast(t('renamed'));
            setDialog(null);
            setAlbum((a) => ({ ...a, name }));
            refresh();
          }}
          onCancel={() => setDialog(null)}
        />
      )}
      {dialog === 'info' && current && (
        <MediaInfoDialog
          media={current}
          onSave={async (data) => {
            const updated = await api.updateKaiMedia(current.id, data);
            toast(t('infoSaved'));
            setDialog(null);
            patchMedia((all) => all.map((m) => (m.id === updated.id ? updated : m)));
            refresh();
          }}
          onCancel={() => setDialog(null)}
        />
      )}
    </>
  );
}

// Xem toàn màn hình: vuốt ngang để lướt như điện thoại, chạm 2 lần để phóng to, hàng ảnh nhỏ ở dưới để lướt nhanh
function MediaViewer({ media, index, onIndexChange, onClose, onShare, isAdmin, onEdit, onDelete }) {
  const t = useT();
  const slider = useRef(null);
  const thumbs = useRef(null);
  const [zoomed, setZoomed] = useState(false);
  const current = media[index];

  // Mở đúng ảnh đã bấm (nhảy ngay, không trượt); khoá cuộn trang phía sau
  useEffect(() => {
    slider.current.scrollLeft = index * slider.current.clientWidth;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ảnh nhỏ đang chọn luôn nằm trong tầm nhìn của hàng ảnh dưới; đổi ảnh thì bỏ phóng to
  useEffect(() => {
    thumbs.current?.children[index]?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    setZoomed(false);
  }, [index]);

  const goTo = (i) => {
    const el = slider.current;
    el.scrollTo({ left: i * el.clientWidth, behavior: 'smooth' });
  };

  // Ảnh đang hiện = vị trí cuộn ngang; đổi ảnh thì dừng video đang phát
  const onScroll = () => {
    const el = slider.current;
    const i = Math.round(el.scrollLeft / el.clientWidth);
    if (i !== index && media[i]) {
      el.querySelectorAll('video').forEach((v) => v.pause());
      onIndexChange(i);
    }
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') (zoomed ? setZoomed(false) : onClose());
      else if (e.key === 'ArrowLeft' && index > 0) goTo(index - 1);
      else if (e.key === 'ArrowRight' && index < media.length - 1) goTo(index + 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  return (
    <div className="kai-viewer-full" role="dialog" aria-modal="true">
      <div className="kai-viewer-top">
        <button className="kai-close" onClick={onClose} aria-label={t('close')}>
          ✕
        </button>
        <span className="kai-counter">
          {index + 1} / {media.length}
        </span>
        <div className="kai-viewer-admin">
          <button onClick={onShare}>↗ {t('share')}</button>
          {isAdmin && (
            <>
              <button onClick={onEdit}>{t('editInfo')}</button>
              <button className="danger" onClick={onDelete}>
                {t('delete')}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="kai-stage">
        <div className={`kai-slider ${zoomed ? 'locked' : ''}`} ref={slider} onScroll={onScroll}>
          {media.map((m, i) => (
            <div key={m.id} className={`kai-slide ${zoomed && i === index ? 'zoomed' : ''}`}>
              {m.type === 'VIDEO' ? (
                <video src={m.url} poster={thumb(m.thumbnailUrl, 1200)} controls playsInline preload="metadata" />
              ) : (
                <>
                  {/* Ảnh nhỏ đã có sẵn từ lưới hiện ngay, ảnh nét (chỉ tải cho ảnh đang xem và 2 ảnh kế bên) đè lên khi tải xong */}
                  {!(zoomed && i === index) && <img className="kai-slide-preview" src={thumb(m.thumbnailUrl, 400)} alt="" />}
                  {Math.abs(i - index) <= 2 && (
                    <img src={imageUrl(m.url, zoomed && i === index ? 3000 : 1600)} alt={m.title || ''} onDoubleClick={() => setZoomed(!zoomed)} />
                  )}
                </>
              )}
            </div>
          ))}
        </div>
        {!zoomed && index > 0 && (
          <button className="kai-nav prev" onClick={() => goTo(index - 1)} aria-label={t('prev')}>
            ‹
          </button>
        )}
        {!zoomed && index < media.length - 1 && (
          <button className="kai-nav next" onClick={() => goTo(index + 1)} aria-label={t('next')}>
            ›
          </button>
        )}
      </div>

      {(current?.title || current?.description) && (
        <div className="kai-caption">
          {current.title && <h2>{current.title}</h2>}
          {current.description && <p>{current.description}</p>}
        </div>
      )}

      <div className="kai-thumbs" ref={thumbs}>
        {media.map((m, i) => (
          <button key={m.id} className={i === index ? 'active' : ''} onClick={() => goTo(i)} aria-label={t('photoN', { n: i + 1 })}>
            <img src={thumb(m.thumbnailUrl, 160)} alt="" loading="lazy" />
            {m.type === 'VIDEO' && <span className="kai-play">▶</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

// Sửa tiêu đề, mô tả của một ảnh/video
function MediaInfoDialog({ media, onSave, onCancel }) {
  const t = useT();
  const [title, setTitle] = useState(media.title || '');
  const [description, setDescription] = useState(media.description || '');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({ title, description });
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="dialog-backdrop" onClick={saving ? undefined : onCancel}>
      <form className="dialog quick-create" onClick={(e) => e.stopPropagation()} onSubmit={save} aria-label={t('editInfo')}>
        <h2>{t('editInfo')}</h2>
        <label>
          {t('title')}
          <input autoFocus maxLength={150} value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>
        <label>
          {t('description')}
          <textarea rows={4} maxLength={1000} value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>
        {error && <p className="error">{error}</p>}
        <div className="dialog-actions">
          <button type="button" onClick={onCancel} disabled={saving}>
            {t('cancel')}
          </button>
          <button type="submit" className="primary" disabled={saving}>
            {saving ? t('saving') : t('save')}
          </button>
        </div>
      </form>
    </div>
  );
}
